import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { useLanguage } from "./LanguageContext";

// --- Types ---
type NavRow = {
  id?: string;
  mes?: string; // e.g. "Enero 2024"
  month?: number; // 1-12
  year?: number;
  value: number;
  period_start?: string | Date;
};

type ContributionRow = {
  id?: string;
  investor_id?: string;
  amount: number; // importe
  contribution_date: string; // ISO date
  capital_percentage?: number; // may be negative for dilution
};

type InvestorRow = {
  id: string;
  capital_invertido: number;
  fecha_inversion: string; // ISO
  porcentaje_capital_social: number;
};

interface InvestmentPoint {
  month: string; // 'jan'..'dec'
  year: number;
  contributed: number; // acumulado
  currentValue: number; // NAV * (percentage/100)
  monthKey: string; // translation key like 'm.jan'
}

interface Props {
  investorId?: string | null;
  className?: string;
  // If true, the parent is still resolving the investor id and
  // the chart should remain in loading state and skip fetching.
  isResolvingInvestor?: boolean;
}

// --- Helpers ---
const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

function monthsBetween(start: Date, end: Date): Date[] {
  const result: Date[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    result.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return result;
}

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

function parseYearMonthFromString(dateStr?: string) {
  if (!dateStr) return null;
  // Accept 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:MM:SSZ' or 'YYYY-MM'
  const ym = dateStr.slice(0, 7);
  const parts = ym.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  const monthIdx = Math.max(0, Math.min(11, month - 1));
  return { year, month: monthIdx };
}

function parseNavRows(rows: NavRow[]): { key: string; value: number }[] {
  return rows.map((r) => {
    // If row has 'mes' like "Enero 2024" try parse, otherwise if has month/year use them
    if (r.mes) {
      const parts = r.mes.split(" ");
      const monthName = parts[0].toLowerCase();
      const year = parseInt(parts[1], 10);
      // monthName spanish -> index
      const monthNamesES = [
        "enero",
        "febrero",
        "marzo",
        "abril",
        "mayo",
        "junio",
        "julio",
        "agosto",
        "septiembre",
        "octubre",
        "noviembre",
        "diciembre",
      ];
      const m = monthNamesES.indexOf(monthName);
      if (m >= 0 && !isNaN(year)) {
        return { key: `${m}-${year}`, value: r.value };
      }
    }

    if (typeof r.month === "number" && typeof r.year === "number") {
      return { key: `${r.month - 1}-${r.year}`, value: r.value };
    }

    // fallback: try to parse mes if it contains numeric year
    return { key: `${0}-${0}`, value: r.value };
  });
}

function getNavDateFromRow(r: NavRow): Date | null {
  if (typeof r.month === 'number' && typeof r.year === 'number') {
    return new Date(r.year, r.month - 1, 1);
  }
  if (r.period_start) {
    const parsed = parseYearMonthFromString(typeof r.period_start === 'string' ? r.period_start : String(r.period_start));
    if (parsed) return new Date(parsed.year, parsed.month, 1);
  }
  if (r.mes) {
    const parts = r.mes.split(' ');
    const monthName = parts[0].toLowerCase();
    const year = parseInt(parts[1], 10);
    const monthNamesES = [
      'enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'
    ];
    const m = monthNamesES.indexOf(monthName);
    if (m >= 0 && !isNaN(year)) return new Date(year, m, 1);
  }
  return null;
}

// --- Main component ---
export function InvestmentEvolutionChart({ investorId, className = "", isResolvingInvestor = false }: Props) {
  const { t, language } = useLanguage();

  const [loading, setLoading] = useState<boolean>(isResolvingInvestor ?? true);
  const [error, setError] = useState<string | null>(null);

  const [investor, setInvestor] = useState<InvestorRow | null>(null);
  const [contributions, setContributions] = useState<ContributionRow[]>([]);
  const [navRows, setNavRows] = useState<NavRow[]>([]);
  const [resolvedInvestorId, setResolvedInvestorId] = useState<string | null>(investorId ?? null);
  const [resolvingInvestor, setResolvingInvestor] = useState(false);
  // Tooltip & interactions must be declared at top-level so hooks order is stable
  const [hovered, setHovered] = useState<{ data: InvestmentPoint; lineType: 'contributed' | 'currentValue' } | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  // Fetch all needed data from Supabase (uses RPC fallbacks when available)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);

      // If parent is still resolving investor id, skip fetching and keep loading
      if (isResolvingInvestor) {
        return;
      }

      // If there's no investor id after resolving, clear data and stop
      if (!investorId) {
        if (!cancelled) {
          setInvestor(null);
          setContributions([]);
          setNavRows([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      try {
        // 1) try to fetch from 'investors' table, fallback to 'users'
        let investorData: any = null;
        const { data: invResp, error: invError } = await supabase
          .from("investor_profiles")
          .select("*")
          .eq("id", investorId)
          .maybeSingle();

        if (invError) {
          // fallback to users table
          const { data: uData, error: uError } = await supabase
            .from("users")
            .select("*")
            .eq("id", investorId)
            .maybeSingle();
          if (uError) throw uError;
          investorData = uData;
        } else {
          investorData = invResp;
        }

        if (!investorData) {
          throw new Error("Investor not found");
        }

        // 2) try RPC get_contributions_by_investor, fallback to direct table
        let contributionsDataRaw: any[] = [];
        try {
          const { data: rpcContrib, error: rpcContribErr } = await supabase.rpc(
            "get_contributions_by_investor",
            { p_investor_id: investorId }
          );
          if (rpcContribErr) throw rpcContribErr;
          contributionsDataRaw = (rpcContrib as any[]) || [];
        } catch {
          const { data: contribRows, error: contribErr } = await supabase
            .from("capital_contributions")
            .select("*")
            .eq("investor_id", investorId)
            .order("contribution_date", { ascending: true });
          if (contribErr) throw contribErr;
          contributionsDataRaw = (contribRows as any[]) || [];
          
        }

        // Normalize contributions to ContributionRow shape
        const contribs: ContributionRow[] = (contributionsDataRaw || []).map((c: any) => ({
          id: c.id,
          investor_id: c.investor_id || c.investorId,
          amount: Number(c.amount || 0),
          contribution_date: c.contribution_date || c.contributionDate || c.fecha || "",
          capital_percentage: typeof c.capital_percentage !== "undefined" ? Number(c.capital_percentage) : (c.capitalPercentage !== undefined ? Number(c.capitalPercentage) : 0),
        }));

        // 3) try RPC get_nav_months, fallback to nav_general / nav_values tables
        let navRaw: any[] = [];
        try {
          const { data: rpcNav, error: rpcNavErr } = await supabase.rpc("get_nav_months", {
            p_period_start: null,
            p_period_end: null,
          });
          if (rpcNavErr) throw rpcNavErr;
          navRaw = (rpcNav as any[]) || [];
        } catch {
          // try nav_general
          const { data: nav1, error: nav1Err } = await supabase
            .from("nav_general")
            .select("*");
          if (!nav1Err && nav1 && nav1.length > 0) {
            navRaw = nav1 as any[];
          } else {
            const { data: nav2, error: nav2Err } = await supabase
              .from("nav_values")
              .select("*");
            if (nav2Err) throw nav2Err;
            navRaw = (nav2 as any[]) || [];
          }
        }

        // Map different nav shapes into NavRow so parseNavRows() can handle them
        const navRowsMapped: NavRow[] = (navRaw || []).map((n: any) => {
          // Prefer explicit month/year fields if present
          if (typeof n.month === "number" && typeof n.year === "number") {
            return { id: n.id, month: n.month, year: n.year, value: Number(n.value ?? n.nav_value ?? n.total_asset_value ?? 0), period_start: n.period_start || null };
          }

          // If period_start/period_month provided convert to month/year using string-safe parse
          const period = n.period_start || n.period_month || n.period || n.fecha || n.date;
          if (period) {
            const p = parseYearMonthFromString(typeof period === 'string' ? period : String(period));
            if (p) {
              return { id: n.id, month: p.month + 1, year: p.year, value: Number(n.value ?? n.nav_value ?? n.total_asset_value ?? 0), period_start: `${p.year}-${pad(p.month + 1)}-01` };
            }
          }

          // If mes string like "Enero 2024" exists keep as-is so parseNavRows can parse
          if (n.mes || n.mes_es || n.mes_espanol) {
            return { id: n.id, mes: n.mes || n.mes_es || n.mes_espanol, value: Number(n.value ?? n.nav_value ?? n.total_asset_value ?? 0), period_start: n.period_start || null };
          }

          // Fallback to raw value
          return { id: n.id, value: Number(n.value ?? n.nav_value ?? n.total_asset_value ?? 0), period_start: n.period_start || null };
        });

        if (!cancelled) {
          setInvestor(investorData as InvestorRow);
          setContributions(contribs);
          setNavRows(navRowsMapped);
        }
      } catch (err: any) {
        console.error(err);
        if (!cancelled) setError(err?.message || "Error loading data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [investorId, isResolvingInvestor]);

  // Build nav map key => value
  const navMap = useMemo(() => {
    const parsed = parseNavRows(navRows);
    const map = new Map<string, number>();
    parsed.forEach((p) => map.set(p.key, p.value));
    return map;
  }, [navRows]);

  // Calculate investment series once data is loaded
  const series: InvestmentPoint[] = useMemo(() => {
    if (!investor) return [];

    // parse investor date safely (avoid timezone shifts)
    const invDateRaw = investor.fecha_inversion || new Date().toISOString();
    const invParsed = parseYearMonthFromString(invDateRaw);
    if (!invParsed) return [];
    const invDate = new Date(invParsed.year, invParsed.month, 1);

    // Determine earliest NAV date (if any) and use the earliest between investor date and NAV start
    let earliestNavDate: Date | null = null;
    for (const nr of navRows) {
      const d = getNavDateFromRow(nr);
      if (d && (!earliestNavDate || d.getTime() < earliestNavDate.getTime())) earliestNavDate = d;
    }

    const startDate = earliestNavDate && earliestNavDate.getTime() < invDate.getTime() ? earliestNavDate : invDate;

    const now = new Date();
    const months = monthsBetween(startDate, now);

    // contributions normalized: keep contribution_date and sort by ISO date string (YYYY-MM-DD)
    const normalizedContribs = contributions
      .map((c) => ({ ...c, contribution_date: c.contribution_date }))
      .sort((a, b) => {
        const aKey = (a.contribution_date || '').slice(0, 10);
        const bKey = (b.contribution_date || '').slice(0, 10);
        if (aKey < bKey) return -1;
        if (aKey > bKey) return 1;
        return 0;
      });

    // We'll index contributions per month and also add the investor's initial capital and percentage
    const contribsByMonth = new Map<string, { amount: number; capital_percentage: number }>();
    normalizedContribs.forEach((c) => {
      const parsed = parseYearMonthFromString(c.contribution_date);
      if (!parsed) return;
      const k = `${parsed.month}-${parsed.year}`; // month is 0-based here
      const prev = contribsByMonth.get(k) || { amount: 0, capital_percentage: 0 };
      prev.amount += Number(c.amount || 0);
      prev.capital_percentage += Number(c.capital_percentage || 0);
      contribsByMonth.set(k, prev);
    });

    // Treat investor.capital_invertido and porcentaje_capital_social as occurring at fecha_inversion
    // Treat investor.capital_invertido and porcentaje_capital_social as occurring at fecha_inversion
    const invParsed2 = parseYearMonthFromString(investor.fecha_inversion);
    if (invParsed2) {
      const invKey = `${invParsed2.month}-${invParsed2.year}`;
      const prev = contribsByMonth.get(invKey) || { amount: 0, capital_percentage: 0 };
      prev.amount += Number(investor.capital_invertido || 0);
      prev.capital_percentage += Number(investor.porcentaje_capital_social || 0);
      contribsByMonth.set(invKey, prev);
    }

    let lastKnownNav = 0;
    let cumulativeContributed = 0;
    let cumulativePercentage = 0; // start at 0 and add investor porcentaje at its month

    const points: InvestmentPoint[] = months.map((mDate) => {
      const y = mDate.getFullYear();
      const mi = mDate.getMonth(); // 0-11
      const monthKey = `m.${MONTH_KEYS[mi]}`;

      const monthKeyStr = `${mi}-${y}`;
      const entry = contribsByMonth.get(monthKeyStr) || { amount: 0, capital_percentage: 0 };

      // accumulate contributions and percentage only when they occur
      cumulativeContributed += entry.amount;
      cumulativePercentage += entry.capital_percentage;

      // find NAV for this month; if missing use lastKnownNav
      const navForKey = navMap.get(monthKeyStr);
      const navValue = typeof navForKey === 'number' ? navForKey : 0;
      if (navValue > 0) lastKnownNav = navValue;
      const effectiveNav = navValue > 0 ? navValue : lastKnownNav;

      // compute current value as NAV * accumulated percentage
      const currentValue = effectiveNav > 0 ? effectiveNav * (cumulativePercentage / 100) : cumulativeContributed;

      return {
        month: MONTH_KEYS[mi],
        year: y,
        contributed: cumulativeContributed,
        currentValue,
        monthKey,
      };
    });

    return points;
  }, [investor, contributions, navMap, navRows]);

  // Derived KPIs
  const lastPoint = series[series.length - 1];
  const startContributed = series[0]?.contributed || 0;
  const endContributed = lastPoint?.contributed || 0;
  const endCurrentValue = lastPoint?.currentValue || 0;
  const totalGains = endCurrentValue - endContributed;
  const gainPercentage = endContributed > 0 ? (totalGains / endContributed) * 100 : 0;
  const totalCapitalPercentage = investor ? (investor.porcentaje_capital_social ?? 0) + (contributions.reduce((acc, c) => acc + (c.capital_percentage ?? 0), 0)) : 0;

  // Simple loading / error UI
  if (loading) {
    // Render a skeleton that preserves the full chart layout so the page
    // doesn't flash a small "Loading..." text before the real chart appears.
    return (
      <div className={`bg-white border border-[#E8EDF4] rounded-2xl overflow-hidden shadow-premium ${className}`}>
        <div className="p-6 pb-4 border-b border-gray-50">
          <div className="h-6 w-1/3 bg-gray-200 rounded animate-pulse mb-3" />
          <div className="h-3 w-2/3 bg-gray-200 rounded animate-pulse mb-6" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="space-y-2">
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-10 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-36 bg-gray-200 rounded animate-pulse" />
            </div>

            <div className="space-y-2">
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-10 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-36 bg-gray-200 rounded animate-pulse" />
            </div>

            <div className="space-y-2">
              <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-10 w-36 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>

        <div className="relative w-full px-8 py-6" style={{ minHeight: 400 }}>
          <div className="w-full h-[360px] bg-gradient-to-b from-gray-100 to-gray-50 rounded-md flex items-center justify-center">
            <div className="h-8 w-8 rounded-full bg-gray-300 animate-pulse" />
          </div>

          <div className="flex justify-center space-x-8 mt-4">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-gray-200 rounded" />
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-gray-200 rounded" />
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 bg-white rounded-2xl border border-red-100 text-red-700 ${className}`}>
        <div className="font-semibold">Error</div>
        <div className="text-sm mt-2">{error}</div>
      </div>
    );
  }

  // --- Chart rendering (keeps the previous look & interactions) ---
  const chartHeight = 400;
  const chartWidth = 1000;
  const margin = { top: 30, right: 50, bottom: 60, left: 90 };
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;

  // value range
  const allValues = series.flatMap((d) => [d.contributed, d.currentValue]);
  const maxValue = allValues.length ? Math.max(...allValues) : 0;
  const minValue = 0;
  const valueRange = Math.max(1, maxValue - minValue);

  const getPointPosition = (value: number, index: number) => {
    const normalized = valueRange > 0 ? (value - minValue) / valueRange : 0;
    const x = series.length > 1 ? (index / (series.length - 1)) * plotWidth + margin.left : margin.left + plotWidth / 2;
    const y = chartHeight - margin.bottom - normalized * plotHeight;
    return { x, y };
  };

  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return "";
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const smoothing = 0.25;
      const cpx1 = prev.x + (curr.x - prev.x) * smoothing;
      const cpy1 = prev.y;
      const cpx2 = curr.x - (curr.x - prev.x) * smoothing;
      const cpy2 = curr.y;
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${curr.x} ${curr.y}`;
    }
    return path;
  };

  const contributedPoints = series.map((d, i) => getPointPosition(d.contributed, i));
  const currentValuePoints = series.map((d, i) => getPointPosition(d.currentValue, i));
  const contributedPath = createSmoothPath(contributedPoints);
  const currentValuePath = createSmoothPath(currentValuePoints);

  const updateTooltip = (e: React.MouseEvent) => {
    setTooltipPos({ x: e.clientX, y: e.clientY - 12 });
  };

  // Small Point component inline
  function Point({ cx, cy, data, lineType, color }: { cx: number; cy: number; data: InvestmentPoint; lineType: 'contributed' | 'currentValue'; color: string }) {
    const isHovered = hovered?.data === data && hovered?.lineType === lineType;
    return (
      <g>
        <circle cx={cx} cy={cy} r={isHovered ? 0 : 3} fill={color} style={{ opacity: isHovered ? 0 : 0.9 }} />
        {isHovered && (
          <>
            <circle cx={cx} cy={cy} r={10} fill={color} opacity={0.12} />
            <circle cx={cx} cy={cy} r={4} fill={color} style={{ filter: `drop-shadow(0 0 12px ${color}66)` }} />
            <circle cx={cx} cy={cy} r={1.5} fill="rgba(255,255,255,0.9)" />
          </>
        )}
        <circle
          cx={cx}
          cy={cy}
          r={16}
          fill="transparent"
          className="cursor-pointer"
          onMouseEnter={(e) => {
            setHovered({ data, lineType });
            updateTooltip(e as any);
          }}
          onMouseMove={(e) => updateTooltip(e as any)}
          onMouseLeave={() => setHovered(null)}
        />
      </g>
    );
  }

  const formatCurrency = (v: number) => {
    const rounded = Math.round(v);
    if (language === 'es') return `€${rounded.toLocaleString('es-ES')}`;
    return `€${rounded.toLocaleString('en-US')}`;
  };

  return (
    <div className={`bg-white border border-[#E8EDF4] rounded-2xl overflow-hidden shadow-premium ${className}`}>
      <div className="p-6 pb-4 border-b border-gray-50">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('investment.evolution.title')}</h3>
        <div className="text-sm text-gray-600 mb-6">{t('investment.evolution.subtitle')}</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">{t('investment.summary.totalContributed')}</div>
            <div className="text-3xl font-bold">{formatCurrency(endContributed)}</div>
            <div className="text-sm text-blue-600">{(contributions?.length ?? 0)} {t('investment.metrics.completedContributions')}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">{t('investment.summary.totalGains')}</div>
            <div className="text-3xl font-bold text-green-600">+{formatCurrency(totalGains)}</div>
            <div className="text-sm text-green-600">+{gainPercentage.toFixed(1)}% {t('investment.metrics.performanceOverContributed')}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">% Capital</div>
            <div className="text-3xl font-bold text-emerald-600">{totalCapitalPercentage.toFixed(2)}%</div>
            <div className="text-sm text-emerald-600">Participación actual</div>
          </div>
        </div>
      </div>

      <div className="relative w-full px-8 py-6" style={{ minHeight: chartHeight }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="contribGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.12} />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="currGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10B981" stopOpacity={0.12} />
              <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {Array.from({ length: 5 }).map((_, i) => {
            const y = chartHeight - margin.bottom - (i * (plotHeight / 4));
            return <line key={i} x1={margin.left} y1={y} x2={margin.left + plotWidth} y2={y} stroke="#F1F5F9" strokeWidth={1} />;
          })}

          {/* Areas */}
          <path d={(contributedPath || "") + ` L ${margin.left + plotWidth} ${chartHeight - margin.bottom} L ${margin.left} ${chartHeight - margin.bottom} Z`} fill="url(#contribGrad)" />
          <path d={(currentValuePath || "") + ` L ${margin.left + plotWidth} ${chartHeight - margin.bottom} L ${margin.left} ${chartHeight - margin.bottom} Z`} fill="url(#currGrad)" />

          {/* Lines */}
          <path d={contributedPath} fill="none" stroke="#3B82F6" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
          <path d={currentValuePath} fill="none" stroke="#10B981" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />

          {/* Points contributed */}
          {series.map((d, i) => {
            const pos = getPointPosition(d.contributed, i);
            return <Point key={`c-${i}`} cx={pos.x} cy={pos.y} data={d} lineType="contributed" color="#3B82F6" />;
          })}

          {/* Points currentValue */}
          {series.map((d, i) => {
            const pos = getPointPosition(d.currentValue, i);
            return <Point key={`v-${i}`} cx={pos.x} cy={pos.y} data={d} lineType="currentValue" color="#10B981" />;
          })}

          {/* Y labels */}
          {Array.from({ length: 5 }).map((_, i) => {
            const value = minValue + (valueRange * i) / 4;
            const y = chartHeight - margin.bottom - (i * (plotHeight / 4));
            const label = value >= 1000 ? `€${Math.round(value / 1000)}k` : `€${Math.round(value)}`;
            return (
              <text key={i} x={margin.left - 12} y={y} textAnchor="end" dominantBaseline="middle" style={{ fontSize: 12, fill: '#64748B' }}>
                {label}
              </text>
            );
          })}

          {/* X labels every 2 months */}
          {series.map((d, i) => {
            if (i % 2 !== 0 && i !== series.length - 1) return null;
            const pos = getPointPosition(d.contributed, i);
            return (
              <text key={i} x={pos.x} y={chartHeight - margin.bottom + 20} textAnchor="middle" style={{ fontSize: 12, fill: '#64748B' }}>
                {t(d.monthKey)}
              </text>
            );
          })}
        </svg>

        {/* Legend */}
        <div className="flex justify-center space-x-8 mt-4">
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 bg-blue-500 rounded" />
            <span className="text-sm font-medium text-gray-700">{t('investment.chart.totalContributed')}</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm font-medium text-gray-700">{t('investment.chart.currentValue')}</span>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {hovered && (
        <div className="fixed z-[9999] pointer-events-none" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
          <div className="px-4 py-3 rounded shadow-lg bg-slate-800 text-white">
            <div className="text-xs text-gray-300">{t(hovered.data.monthKey)} {hovered.data.year}</div>
            <div className="font-bold">{hovered.lineType === 'contributed' ? formatCurrency(hovered.data.contributed) : formatCurrency(hovered.data.currentValue)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
